import * as THREE from 'three';
import { TrackRuntime } from '../core/track/runtime';
import { Frame } from '../core/track/frames';
import { sweepProfile, mergeMeshes, SweepMesh } from '../core/geometry/sweep';
import {
  buildGearRackMesh,
  normalizeTrackShapeSettings,
  renderTrackProfile,
  trackLoopsForShape,
  TrackShapeSettings,
} from '../core/geometry/gearRack';
import { spanCount } from '../core/track/spline';

const SPANS_PER_CHUNK = 3;
const SAMPLE_STEP = 0.55; // m between sweep rings (render resolution)
const BLINK_MS = 3000;
const BLINK_HZ = 3;

interface Chunk {
  spanStart: number; // first span in this chunk
  spanEnd: number; // exclusive
  rails: THREE.Mesh;
  spine: THREE.Mesh;
  rack: THREE.Mesh;
}

/**
 * Chunked track mesh: rails + triangular frame (deck + diagonals) + optional
 * gear rack. Edits rebuild only dirty chunks so the track follows the cursor
 * at pointer rate.
 */
export class TrackMesh {
  readonly group = new THREE.Group();
  private chunks: Chunk[] = [];
  private trackShape: TrackShapeSettings = normalizeTrackShapeSettings(undefined);
  private railMat = new THREE.MeshStandardMaterial({ color: 0xf2f3f5, roughness: 0.35, metalness: 0.55 });
  private spineMat = new THREE.MeshStandardMaterial({ color: 0x8b95a3, roughness: 0.55, metalness: 0.5 });
  private rackMat = new THREE.MeshStandardMaterial({ color: 0xc8372d, roughness: 0.45, metalness: 0.55 });
  private blinkMeshes: THREE.Mesh[] = [];
  private blinkMat = new THREE.MeshBasicMaterial({
    color: 0xffee55,
    transparent: true,
    opacity: 0.85,
    depthTest: false,
    depthWrite: false,
  });
  private blinkSpan: number | null = null;
  private blinkUntil = 0;
  private blinkStarted = 0;

  constructor() {
    this.group.name = 'track';
  }

  /** Update track-shape settings used on the next rebuild. */
  setTrackShape(settings: Partial<TrackShapeSettings> | null | undefined): void {
    this.trackShape = normalizeTrackShapeSettings(settings);
  }

  /** @deprecated Use {@link setTrackShape}. */
  setGearRack(settings: Partial<TrackShapeSettings> | null | undefined): void {
    this.setTrackShape(settings);
  }

  /** Full rebuild (load, close-toggle, node count change). */
  rebuildAll(runtime: TrackRuntime): void {
    for (const c of this.chunks) this.disposeChunk(c);
    this.chunks = [];
    const nSpans = spanCount(runtime.centerline.track);
    for (let s0 = 0; s0 < nSpans; s0 += SPANS_PER_CHUNK) {
      const chunk = this.buildChunk(runtime, s0, Math.min(s0 + SPANS_PER_CHUNK, nSpans));
      this.chunks.push(chunk);
    }
    this.refreshBlink(runtime);
  }

  /** Rebuild only the chunks containing any of the given spans. */
  rebuildSpans(runtime: TrackRuntime, dirtySpans: number[]): void {
    const nSpans = spanCount(runtime.centerline.track);
    const expectedChunks = Math.ceil(nSpans / SPANS_PER_CHUNK);
    if (this.chunks.length !== expectedChunks) {
      this.rebuildAll(runtime);
      return;
    }
    const dirtyChunks = new Set<number>();
    for (const s of dirtySpans) dirtyChunks.add(Math.floor(s / SPANS_PER_CHUNK));
    for (const ci of dirtyChunks) {
      const old = this.chunks[ci];
      if (!old) continue;
      this.disposeChunk(old);
      this.chunks[ci] = this.buildChunk(runtime, old.spanStart, old.spanEnd);
    }
    this.refreshBlink(runtime);
  }

  /** Flash the span at arc-length `s` for a few seconds so Checks can point at a piece. */
  blinkAtS(runtime: TrackRuntime, s: number, durationMs = BLINK_MS): void {
    const nSpans = spanCount(runtime.centerline.track);
    if (nSpans <= 0 || runtime.totalLength < 1e-6) {
      this.clearBlink();
      return;
    }
    this.blinkSpan = runtime.centerline.sToSpanU(s).span;
    this.blinkStarted = performance.now();
    this.blinkUntil = this.blinkStarted + durationMs;
    this.refreshBlink(runtime);
  }

  /** Pulse blink opacity; call from the render loop. */
  updateBlink(now: number): void {
    if (this.blinkSpan === null) return;
    if (now >= this.blinkUntil) {
      this.clearBlink();
      return;
    }
    const elapsed = (now - this.blinkStarted) / 1000;
    this.blinkMat.opacity = 0.2 + 0.7 * (0.5 + 0.5 * Math.sin(elapsed * Math.PI * 2 * BLINK_HZ));
  }

  private buildChunk(runtime: TrackRuntime, spanStart: number, spanEnd: number): Chunk {
    const cl = runtime.centerline;
    const nSpans = spanCount(cl.track);
    const s0 = cl.spanStartS(spanStart);
    const s1 = spanEnd >= nSpans ? runtime.totalLength : cl.spanStartS(spanEnd);

    const frames: Frame[] = [];
    const len = Math.max(s1 - s0, 0.01);
    const n = Math.max(2, Math.ceil(len / SAMPLE_STEP));
    for (let i = 0; i <= n; i++) frames.push(runtime.frameAt(s0 + (len * i) / n));

    const profile = renderTrackProfile(this.trackShape);
    const { rails, spine } = trackLoopsForShape(this.trackShape, 'render');
    const railMeshes: SweepMesh[] = rails.map((loop) => sweepProfile(frames, loop));
    const spineMeshes: SweepMesh[] = spine.map((loop) => sweepProfile(frames, loop));
    const rackMesh = buildGearRackMesh(runtime, s0, s1, this.trackShape, profile);

    const chunk: Chunk = {
      spanStart,
      spanEnd,
      rails: this.toMesh(mergeMeshes(railMeshes), this.railMat),
      spine: this.toMesh(mergeMeshes(spineMeshes), this.spineMat),
      rack: this.toMesh(rackMesh, this.rackMat),
    };
    this.group.add(chunk.rails, chunk.spine, chunk.rack);
    return chunk;
  }

  private toMesh(data: SweepMesh, mat: THREE.Material): THREE.Mesh {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
    geo.setIndex(new THREE.BufferAttribute(data.indices, 1));
    const mesh = new THREE.Mesh(geo, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.isTrack = true;
    return mesh;
  }

  private disposeChunk(c: Chunk): void {
    for (const m of [c.rails, c.spine, c.rack]) {
      this.group.remove(m);
      m.geometry.dispose();
    }
  }

  private refreshBlink(runtime: TrackRuntime): void {
    this.disposeBlinkMeshes();
    if (this.blinkSpan === null) return;
    const nSpans = spanCount(runtime.centerline.track);
    if (this.blinkSpan < 0 || this.blinkSpan >= nSpans) {
      this.clearBlink();
      return;
    }
    const chunk = this.buildChunk(runtime, this.blinkSpan, this.blinkSpan + 1);
    const meshes = [chunk.rails, chunk.spine, chunk.rack];
    for (const m of meshes) {
      m.material = this.blinkMat;
      m.castShadow = false;
      m.receiveShadow = false;
      m.renderOrder = 20;
      m.userData.isTrack = false;
    }
    this.blinkMeshes = meshes;
  }

  private clearBlink(): void {
    this.blinkSpan = null;
    this.blinkUntil = 0;
    this.blinkStarted = 0;
    this.disposeBlinkMeshes();
  }

  private disposeBlinkMeshes(): void {
    for (const m of this.blinkMeshes) {
      this.group.remove(m);
      m.geometry.dispose();
    }
    this.blinkMeshes = [];
  }
}
